import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { getBillingPortalUrl } from '../../api/paymentsService';
import useResetOnRestore from '../../hooks/useResetOnRestore';

// Shown app-wide while a renewal has failed but the grace period is still running.
// The account is deliberately NOT locked yet — Stripe is still retrying — so this
// is the only signal the user gets before access actually stops.
//
// The bar is fixed above the (also fixed) TopBar rather than sitting in normal
// flow, where the header covered it as soon as a route mounted. It publishes its
// own height as --past-due-offset so the header and page content shift down.
const PastDueBanner = ({ daysRemaining }) => {
  const [loading, setLoading] = useState(false);
  // Dismissal is intentionally in-memory only: closing it clears the current
  // page, but any reload brings it straight back.
  const [dismissed, setDismissed] = useState(false);
  const barRef = useRef(null);

  useResetOnRestore(useCallback(() => setLoading(false), []));

  useEffect(() => {
    const root = document.documentElement;
    const clear = () => {
      root.style.removeProperty('--past-due-offset');
      document.body.style.removeProperty('padding-top');
    };

    if (dismissed) {
      clear();
      return undefined;
    }

    const el = barRef.current;
    if (!el) return undefined;

    // Height is not fixed — the text wraps to two lines on narrow screens.
    const apply = () => {
      const h = `${el.offsetHeight}px`;
      root.style.setProperty('--past-due-offset', h);
      document.body.style.paddingTop = h;
    };
    apply();

    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      clear();
    };
  }, [dismissed]);

  if (dismissed) return null;

  const handleFix = async () => {
    setLoading(true);
    try {
      const url = await getBillingPortalUrl();
      if (url) {
        window.location.href = url;
        return;
      }
    } catch {
      /* leave the banner in place; Settings has the full error path */
    }
    setLoading(false);
  };

  const dayLabel = daysRemaining === 1 ? '1 day' : `${daysRemaining} days`;

  return (
    <div
      ref={barRef}
      role="alert"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        // Above the TopBar (40) so it can never be covered again.
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.75rem',
        flexWrap: 'wrap',
        // Tokens, not fixed hex: --warn-* has a dark-mode variant, a hardcoded
        // amber slab glares in dark.
        background: 'var(--warn-wash)',
        borderBottom: '1px solid var(--warn)',
        color: 'var(--warn-ink)',
        padding: '0.75rem 2.5rem 0.75rem 1rem',
        paddingTop: 'calc(0.75rem + env(safe-area-inset-top))',
        fontSize: '0.9rem',
      }}
    >
      <AlertTriangle size={18} style={{ flexShrink: 0 }} />
      <span>
        We couldn&apos;t take your last payment. Please update your payment method.
        Access continues for {dayLabel}.
      </span>
      <button
        type="button"
        onClick={handleFix}
        disabled={loading}
        style={{
          background: 'var(--warn-ink)',
          color: 'var(--surface)',
          border: 'none',
          borderRadius: '8px',
          padding: '0.4rem 0.9rem',
          fontWeight: 600,
          cursor: loading ? 'default' : 'pointer',
          opacity: loading ? 0.7 : 1,
        }}
      >
        {loading ? 'Opening…' : 'Update payment method'}
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        title="Dismiss — this will show again next time you open Routes"
        style={{
          position: 'absolute',
          top: 'calc(0.5rem + env(safe-area-inset-top))',
          right: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          background: 'transparent',
          border: 'none',
          borderRadius: '8px',
          color: 'var(--warn-ink)',
          cursor: 'pointer',
        }}
      >
        <X size={18} />
      </button>
    </div>
  );
};

export default PastDueBanner;
